const AUTO_CLOSE_MS = 5000;

function getDefaultSuccessMessage() {
  const localized = window.rocketCf7Modal?.successMessage;
  if (typeof localized === "string" && localized.trim() !== "") {
    return localized.trim();
  }

  return "Thank you for your message. It has been sent.";
}

function getWpcf7Root(target) {
  if (!(target instanceof Element)) {
    return null;
  }

  if (target.classList.contains("wpcf7")) {
    return target;
  }

  return target.closest(".wpcf7");
}

function getModal(element) {
  return element?.closest(".global-modal") ?? null;
}

function getResponseMessage(event, wpcf7Root) {
  const apiMessage = event?.detail?.apiResponse?.message;
  if (typeof apiMessage === "string" && apiMessage.trim() !== "") {
    return apiMessage.trim();
  }

  const output = wpcf7Root?.querySelector(".wpcf7-response-output");
  const outputMessage = output?.textContent.replace(/\s+/g, " ").trim();
  if (outputMessage) {
    return outputMessage;
  }

  const srMessage = wpcf7Root
    ?.querySelector(".screen-reader-response [role='status']")
    ?.textContent.replace(/\s+/g, " ")
    .trim();

  return srMessage || getDefaultSuccessMessage();
}

function scrollModalDialog(modal) {
  const dialog = modal?.querySelector(".global-modal__dialog");
  if (!dialog) {
    return;
  }

  dialog.scrollTop = 0;
}

function clearAutoClose(modal) {
  if (!(modal instanceof HTMLElement) || !modal.__cf7AutoCloseTimer) {
    return;
  }

  window.clearTimeout(modal.__cf7AutoCloseTimer);
  modal.__cf7AutoCloseTimer = null;
}

function resetCf7Form(wpcf7Root) {
  const form = wpcf7Root?.querySelector("form");
  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  if (typeof window.wpcf7 !== "undefined" && typeof window.wpcf7.reset === "function") {
    window.wpcf7.reset(form);
  }
}

export function resetCf7ModalSuccess(modal) {
  if (!(modal instanceof HTMLElement)) {
    return;
  }

  clearAutoClose(modal);
  modal.classList.remove("is-cf7-success");
  modal.querySelector("[data-cf7-success-panel]")?.remove();

  modal.querySelector(".global-modal__title")?.removeAttribute("hidden");
  modal.querySelector("[data-service-modal-context]")?.removeAttribute("hidden");
  modal.querySelector(".wpcf7")?.removeAttribute("hidden");

  const wpcf7Root = modal.querySelector(".wpcf7");
  if (wpcf7Root) {
    resetCf7Form(wpcf7Root);
  }
}

function closeModal(modal) {
  if (!(modal instanceof HTMLElement)) {
    return;
  }

  const closeTrigger = modal.querySelector(
    "[data-global-modal-close], [data-service-modal-close]",
  );

  if (closeTrigger instanceof HTMLElement) {
    closeTrigger.click();
  }
}

function showSuccessPanel(modal, message) {
  const body = modal.querySelector(".global-modal__body");
  if (!body) {
    return;
  }

  body.querySelector("[data-cf7-success-panel]")?.remove();

  const panel = document.createElement("div");
  panel.className = "global-modal__success-panel";
  panel.setAttribute("data-cf7-success-panel", "");
  panel.setAttribute("role", "status");
  panel.setAttribute("tabindex", "-1");
  panel.textContent = message;
  body.appendChild(panel);

  modal.classList.add("is-cf7-success");
  scrollModalDialog(modal);
  panel.focus({ preventScroll: true });
}

function showSuccessAndAutoClose(wpcf7Root, event) {
  const modal = getModal(wpcf7Root);
  if (!modal) {
    return;
  }

  const message = getResponseMessage(event, wpcf7Root);
  showSuccessPanel(modal, message);

  clearAutoClose(modal);
  modal.__cf7AutoCloseTimer = window.setTimeout(() => {
    resetCf7ModalSuccess(modal);
    closeModal(modal);
  }, AUTO_CLOSE_MS);
}

function handleCf7ModalResponse(event) {
  const wpcf7Root = getWpcf7Root(event.target);
  const modal = getModal(wpcf7Root);
  if (!modal) {
    return;
  }

  if (event.type === "wpcf7mailsent") {
    requestAnimationFrame(() => {
      showSuccessAndAutoClose(wpcf7Root, event);
    });
    return;
  }

  if (event.type === "wpcf7invalid" || event.type === "wpcf7spam" || event.type === "wpcf7mailfailed") {
    resetCf7ModalSuccess(modal);
    requestAnimationFrame(() => {
      scrollModalDialog(modal);
    });
  }
}

function handleModalCloseClick(event) {
  const closeTrigger = event.target.closest(
    "[data-global-modal-close], [data-service-modal-close]",
  );

  if (!closeTrigger) {
    return;
  }

  const modal = closeTrigger.closest(".global-modal");
  if (modal) {
    resetCf7ModalSuccess(modal);
  }
}

export default function initCf7ModalResponse() {
  if (document.documentElement.dataset.cf7ModalResponseBound === "1") {
    return;
  }

  document.documentElement.dataset.cf7ModalResponseBound = "1";
  document.addEventListener("click", handleModalCloseClick, true);

  ["wpcf7mailsent", "wpcf7invalid", "wpcf7spam", "wpcf7mailfailed"].forEach((eventName) => {
    document.addEventListener(eventName, handleCf7ModalResponse);
  });
}
